import { supabase } from "@/lib/supabase"

// const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

// function getFunctionsUrl(name: string): string {
//   if (!SUPABASE_URL) throw new Error("VITE_SUPABASE_URL is not set")
//   return `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1/${name}`
// }

async function callEdgeFunction<T>(
  name: string,
  body: Record<string, unknown>
): Promise<{ data: T | null; error: string | null }> {
  const { data, error } = await supabase.functions.invoke(name, {
    body,
  })

  if (error) {
    return { data: null, error: error.message || "Request failed" }
  }

  return { data: data as T, error: null }
}

export interface CreateStripeAccountResponse {
  account_id: string
}

export async function connectStripeAccount(
  orgId: string
): Promise<{ data: CreateStripeAccountResponse | null; error: string | null }> {
  return callEdgeFunction<CreateStripeAccountResponse>("create-stripe-account", {
    org_id: orgId,
  })
}

/** Message type sent when Stripe Connect popup closes so the opener can refresh org data. */
export const STRIPE_CONNECT_COMPLETE = "STRIPE_CONNECT_COMPLETE"

export interface CreateStripeOnboardingLinkResponse {
  url: string
}

export async function getStripeOnboardingLink(
  orgId: string,
  returnBaseUrl?: string
): Promise<{ data: CreateStripeOnboardingLinkResponse | null; error: string | null }> {
  const body: Record<string, unknown> = { org_id: orgId }
  if (returnBaseUrl) body.return_base_url = returnBaseUrl
  return callEdgeFunction<CreateStripeOnboardingLinkResponse>(
    "create-stripe-onboarding-link",
    body
  )
}

// ---------------------------------------------------------------------------
// NOS-194 (C2): connect an EXISTING Stripe account via OAuth + donor discovery
// ---------------------------------------------------------------------------

export interface StripeOAuthAuthorizeResponse {
  url: string
}

/**
 * Ask connect-existing-stripe for the Stripe OAuth authorize URL (server
 * generates + stores the CSRF state). Open the returned URL with
 * openStripeOnboardingPopup — Stripe redirects the popup back to the
 * /stripe/connect/callback route, which completes the exchange.
 */
export async function getStripeOAuthUrl(
  orgId: string,
  returnBaseUrl?: string
): Promise<{ data: StripeOAuthAuthorizeResponse | null; error: string | null }> {
  const body: Record<string, unknown> = { action: "authorize", org_id: orgId }
  if (returnBaseUrl) body.return_base_url = returnBaseUrl
  return callEdgeFunction<StripeOAuthAuthorizeResponse>("connect-existing-stripe", body)
}

export interface StripeOAuthExchangeResponse {
  account_id: string
  org_id: string
  charges_enabled: boolean
  details_submitted: boolean
  onboarding_complete: boolean
}

/** Exchange the OAuth callback code+state for the connected account (server-side). */
export async function exchangeStripeOAuthCode(
  code: string,
  state: string
): Promise<{ data: StripeOAuthExchangeResponse | null; error: string | null }> {
  return callEdgeFunction<StripeOAuthExchangeResponse>("connect-existing-stripe", {
    action: "exchange",
    code,
    state,
  })
}

/** Disconnect an OAuth-connected (Standard) Stripe account so another can be connected. */
export async function disconnectStripeAccount(
  orgId: string
): Promise<{ data: { disconnected: boolean } | null; error: string | null }> {
  return callEdgeFunction<{ disconnected: boolean }>("connect-existing-stripe", {
    action: "disconnect",
    org_id: orgId,
  })
}

export interface DiscoverStripeDonorsResponse {
  customers_found: number
  contacts_created: number
  contacts_updated: number
  skipped_no_email: number
  subscriptions_adopted: number
  saved_pm_no_subscription: {
    count: number
    customers: { stripe_customer_id: string; email: string | null; name: string | null }[]
  }
  truncated: boolean
}

/**
 * Owner-triggered, idempotent, read-only discovery of existing donors on the
 * connected Stripe account (customers → contacts, active subscriptions →
 * adopted recurring gifts, saved-PM-without-subscription list for C3).
 */
export async function discoverStripeDonors(
  orgId: string
): Promise<{ data: DiscoverStripeDonorsResponse | null; error: string | null }> {
  return callEdgeFunction<DiscoverStripeDonorsResponse>("discover-stripe-donors", {
    org_id: orgId,
  })
}

/**
 * Opens the Stripe onboarding URL in a popup. When the popup closes, posts
 * STRIPE_CONNECT_COMPLETE so the opener can refresh org data (e.g. stripe_onboarding_complete).
 */
export function openStripeOnboardingPopup(url: string): void {
  const popup = window.open(url, "_blank", "noopener,noreferrer")
  if (!popup) return
  const pollTimer = setInterval(() => {
    if (popup.closed) {
      clearInterval(pollTimer)
      window.postMessage({ type: STRIPE_CONNECT_COMPLETE }, window.location.origin)
    }
  }, 500)
}
